import { useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { useParams } from 'react-router-dom'

import {
  MONITORING_DEVICES,
  OPERATION_FIND_SITE
} from '../../operations-gql/queries'
import { resolveEslAvailableLevel } from '../../helpers'

const MAP_DEVICE_TYPES = [
  'ap',
  'switch',
  'esl',
  'chromecast',
  'tv',
  'firewall',
  'rack',
  'cctv_camera',
  'cctv_server',
  'power-inverter',
  'wibox',
  'wifirst-server'
]

const findBuildingAndFloor = (floorId, buildings) => {
  const building = buildings.find(
    ({ maps }) => maps.findIndex(({ uuid }) => uuid === floorId) > -1,
  )
  if (building) {
    const floor = building.maps.find(({ uuid }) => uuid === floorId)
    return {
      floor,
      building,
    }
  }
}

const useSite = () => {
  const {focusId, floorId} = useParams()
  const site = useQuery(OPERATION_FIND_SITE, {
    variables: { sf_id: focusId },
  })
  const devices = useQuery(MONITORING_DEVICES, {
    variables: { sf_ids: [focusId] },
  })
  const loading = useMemo(() => {
    return devices.loading || site.loading
  }, [devices.loading, site.loading])

  const isSiteDown = useMemo(() => {
    if (loading || !site.data?.site) return false
    return site.data.site.equipment?.available_level === 'down'
  }, [loading, site.data])

  const equipments = useMemo(() => {
    if (loading) {
      return []
    } else if (!site.data || !site.data.site) {
      return []
    }
    const allEquipments = Object.entries(
      devices.data?.equipments ?? {},
    ).flatMap(([, equipments]) => equipments)
    const siteDevices = site.data.site.devices

    const getEslStatus = (eslDevice, parentDevice = null) => {
      return resolveEslAvailableLevel({
        eslDevice,
        parentDevice,
        allEquipments
      })
    }

    const processedDevices = siteDevices
     .filter(
        (device) =>
          Boolean(device.model) &&
          device.model?.type &&
          MAP_DEVICE_TYPES.includes(device.model.type),
      )
      .map((device) => {
        const surfaceAreas = site?.data?.site?.surface_areas
        const { floor, building } =
          device.position?.map && surfaceAreas
            ? findBuildingAndFloor(
              device.position?.map?.uuid,
              surfaceAreas,
            ) || { floor: null, building: null }
            : { floor: null, building: null }

        const processedChildren = device.children ? device.children
          .map(child => {
            const monitoringChild = allEquipments.find(eq => eq.uuid === child.uuid)
            const eslStatus = child.model?.type === 'esl' ? getEslStatus(child, device) : null

            if (monitoringChild) {
              return {
                ...child,
                available_level: isSiteDown ? 'unknown' : (eslStatus ?? monitoringChild.available_level),
                is_reachable: isSiteDown ? false : (eslStatus ? ['installed', 'up'].includes(eslStatus) : monitoringChild.is_reachable),
                ip_address: monitoringChild.ip_address,
                mac: monitoringChild.mac,
                manufacturer: monitoringChild.manufacturer,
                battery_charge: monitoringChild.battery_charge,
                battery_time_remaining: monitoringChild.battery_time_remaining,
                total_load: monitoringChild.total_load,
                battery_status: monitoringChild.battery_status,
                battery_replace_date: monitoringChild.battery_replace_date,
                model: {
                  ...child.model,
                  name: monitoringChild.model,
                  manufacturer: monitoringChild.manufacturer
                }
              }
            }
            return {
              ...child,
              available_level: isSiteDown ? 'unknown' : (eslStatus ?? child.status ?? 'down'),
              is_reachable: eslStatus ? ['installed', 'up'].includes(eslStatus) : false
            }
          }) : []

        return {
          ...device,
          model: device.model,
          floor,
          building,
          available_level: isSiteDown ? 'unknown' : device?.status,
          children: processedChildren
        }
      })
      .map((device) => {
        const monitoringEquipment = allEquipments.find(
          ({ uuid }) => device.uuid && device.uuid === uuid,
        )
        const eslStatus = device.model?.type === 'esl' && !device.parent ? getEslStatus(device) : null

        if (monitoringEquipment) {
          return {
            ...device,
            model: {
              ...device.model,
              name: monitoringEquipment.model,
              manufacturer: monitoringEquipment.manufacturer
            },
            manufacturer: monitoringEquipment.manufacturer,
            is_reachable: isSiteDown ? false : (eslStatus ? ['installed', 'up'].includes(eslStatus) : monitoringEquipment.is_reachable),
            ip_address: monitoringEquipment.ip_address,
            mac: monitoringEquipment.mac,
            channel_a: monitoringEquipment.channel_a,
            channel_b: monitoringEquipment.channel_g,
            available_level: isSiteDown ? 'unknown' : (eslStatus ?? monitoringEquipment.available_level),
            connected_stations: monitoringEquipment.connected_stations,
            ssid_names: monitoringEquipment.ssid_names,
            ssids: monitoringEquipment.ssids,
            total_tunnels: monitoringEquipment.total_tunnels,
            tunnels_up: monitoringEquipment.tunnels_up,
            system_uptime: monitoringEquipment.system_uptime,
            battery_charge: monitoringEquipment.battery_charge,
            battery_time_remaining: monitoringEquipment.battery_time_remaining,
            total_load: monitoringEquipment.total_load,
            battery_status: monitoringEquipment.battery_status,
            battery_replace_date: monitoringEquipment.battery_replace_date,
            parent_label: monitoringEquipment.parent_label,
            parent_mac: monitoringEquipment.parent_mac,
            parent_port: monitoringEquipment.parent_port,
            parent_status: monitoringEquipment.parent_status,
            parent_is_reachable: monitoringEquipment.parent_is_reachable,
          }
        }
        
        if (device.model?.type === 'esl' && !device.parent) {
          return {
            ...device,
            available_level: isSiteDown ? 'unknown' : eslStatus,
            is_reachable: eslStatus ? ['installed', 'up'].includes(eslStatus) : false
          }
        }
        
        return device
      })

    const parentDevices = processedDevices.filter(device => device.parent === null || !processedDevices.some(({uuid}) => uuid === device.parent))

    return parentDevices
  }, [loading, site.data, devices.data, isSiteDown])

  return {
    loading,
    equipments,
    mapEquipments: equipments.filter(
      ({ position }) => position && position?.map?.uuid === floorId,
    ),
    site: !site.loading ? site?.data?.site : null,
    isSiteDown,
    lastSeen: site?.data?.site?.equipment?.last_seen
  }
}

export default useSite
